import { useTranslation } from 'react-i18next';
import { es, enUS } from 'date-fns/locale';
import { format } from 'date-fns';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { plannedToAnchorDate } from '@/shared/lib/datetime/planned-date';
import type { TimelineMilestoneDto } from '../../types';

const MILESTONE_COLOR = '#7c3aed';

interface TimelineMilestoneMarkerProps {
  milestone: TimelineMilestoneDto;
  leftPx: number;
}

/** A calendar milestone (all-day `CalendarEvent`) rendered as a diamond marker on a person's row. */
export function TimelineMilestoneMarker({ milestone, leftPx }: TimelineMilestoneMarkerProps) {
  const { t, i18n } = useTranslation('tasks');
  const locale = i18n.language.startsWith('es') ? es : enUS;
  const dateLabel = format(plannedToAnchorDate(milestone.date), 'd MMM', { locale });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          data-testid="tasks-timeline-milestone"
          className="absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1"
          style={{ left: leftPx }}
        >
          <span
            className="size-3.5 shrink-0 rotate-45 rounded-[2px]"
            style={{ backgroundColor: MILESTONE_COLOR }}
          />
          <p className="whitespace-nowrap text-xs font-medium" style={{ color: MILESTONE_COLOR }}>
            {milestone.title}
          </p>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        {t('timeline.milestone.tooltip', { title: milestone.title, date: dateLabel })}
      </TooltipContent>
    </Tooltip>
  );
}
